// ClaimDisbursementForm.jsx
import {
  Box,
  Button,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  CircularProgress,
} from "@mui/material";
import { Formik, useFormikContext } from "formik";
import * as yup from "yup";
import Swal from "sweetalert2";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useState, useEffect, useRef } from "react";
import { allAccountSubCategories, currency, allPaymentTypes } from "../../../data/Axios/queries";
import { accountsmanagement, HRService } from "../../../config";
import Header from "../../../components/Header";
import { fetchDataEngine, generateAndExecuteMutation, graphqlQuery } from "../../../data/Axios/DynamicService";

const base_url = accountsmanagement.uri;
const hr_base_url = HRService.uri;

const validationSchema = yup.object().shape({
  disbursementAccountType: yup.number().required("Account Type is required"),
  disbursementBankAccountId: yup.number().required("Bank Account is required"),
  //paymentReference: yup.string().required("Payment Reference is required"),
  currencyId: yup.number().required("Currency is required"),
  exchangeRate: yup.number().min(0).required("Exchange Rate is required"),
  paymentMethod: yup.string().required("Payment Method is required"),
});


const AutoFillExchangeRate = ({ currencies }) => {
  const { values, setFieldValue } = useFormikContext();
  const prevCurrency = useRef();

  useEffect(() => {
    if (values.currencyId && values.currencyId !== prevCurrency.current) {
      prevCurrency.current = values.currencyId;
      const cur = currencies.find((c) => c.id === Number(values.currencyId));
      setFieldValue("exchangeRate", cur?.exchangeRate ?? 1.0);
    }
  }, [values.currencyId, currencies, setFieldValue]);

  return null;
};

const ClaimDisbursementForm = ({ data, onClose, refetchData }) => {
  const isNonMobile = useMediaQuery("(min-width:600px)");
  const [accountTypes, setAccountTypes] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);

  const decodedToken = JSON.parse(localStorage.getItem("decodedToken") || "{}");
  const disbursedBy = Number(decodedToken.Id) || null;

  // -----------------------------------------------------------------
  // FETCH: Account Types, Currencies, Payment Methods
  // -----------------------------------------------------------------
  useEffect(() => {
    const fetchStatic = async () => {
      try {
        const [accTypes, curs, pays] = await Promise.all([
          graphqlQuery(allAccountSubCategories, base_url),
          graphqlQuery(currency, base_url),
          graphqlQuery(allPaymentTypes, base_url),
        ]);
        setAccountTypes(accTypes || []);
        setCurrencies(curs || []);
        setPaymentMethods(pays || []);
      } catch (err) {
        console.error(err);
      }
    };
    fetchStatic();
  }, []);

  // -----------------------------------------------------------------
  // FETCH: Bank Accounts when Account Type changes
  // -----------------------------------------------------------------
  const fetchBankAccounts = async (accountTypeId) => {
    if (!accountTypeId) {
      setBankAccounts([]);
      return;
    }
    setLoadingAccounts(true);
    try {
      const accounts = await fetchDataEngine(
        "allAccountsByColumnValues",
        "accounts",
        { accountSubcategoryId: Number(accountTypeId) },
        ["id", "accountNumber", "accountName"],
        base_url
      );
      setBankAccounts(accounts || []);
    } catch (err) {
      console.error(err);
      setBankAccounts([]);
    } finally {
      setLoadingAccounts(false);
    }
  };

  // -----------------------------------------------------------------
  // Initial Values
  // -----------------------------------------------------------------
  const initialValues = {
    id: data?.id || null,
    disbursementAccountType: "",
    disbursementBankAccountId: "",
    paymentReference: "",
    paymentMethod: "",
    currencyId: data?.currencyId || "",
    exchangeRate: data?.exchangeRate || 1.0,
  };

  // -----------------------------------------------------------------
  // Submit
  // -----------------------------------------------------------------
  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const payload = {
        id: values.id,
        disbursedBy,
        isdisbursed: 1,
        currencyId: Number(values.currencyId),
        exchangeRate: Number(values.exchangeRate),
        paymentReference: values.paymentReference,
        paymentMethod: Number(values.paymentMethod),
        disbursementAccountType: Number(values.disbursementAccountType),
        disbursementBankAccountId: Number(values.disbursementBankAccountId),
      };

      const response = await generateAndExecuteMutation(
        "disburseApprovedStaffClaim",
        payload,
        "approveModel",
        hr_base_url
      );

      if (response?.disburseApprovedStaffClaim) {
        Swal.fire({
          icon: "success",
          title: "Disbursed!",
          text: "Claim has been disbursed successfully.",
        });
        refetchData?.();
        onClose?.();
      } else {
        throw new Error("No data returned");
      }
    } catch (err) {
      console.error(err);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to disburse claim.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box m="20px">
      <Header title="Disburse Claim" subtitle="Fill payment details" />

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          setFieldValue,
          isSubmitting,
        }) => (
          <form onSubmit={handleSubmit}>
            {/* Auto-fill exchange rate */}
            <AutoFillExchangeRate currencies={currencies} />

            <Box
              display="grid"
              gap="30px"
              gridTemplateColumns="repeat(4, minmax(0, 1fr))"
              sx={{
                "& > div": { gridColumn: isNonMobile ? undefined : "span 4" },
              }}
            >
              {/* Account Type */}
              <FormControl fullWidth variant="filled" sx={{ gridColumn: "span 2" }}>
                <InputLabel>Account Type *</InputLabel>
                <Select
                  name="disbursementAccountType"
                  value={values.disbursementAccountType}
                  onChange={(e) => {
                    handleChange(e);
                    fetchBankAccounts(e.target.value);
                    setFieldValue("disbursementBankAccountId", "");
                  }}
                  error={touched.disbursementAccountType && !!errors.disbursementAccountType}
                >
                  {accountTypes
                    .filter((subCat) => {
                      const catId = parseInt(subCat.accountCategoryId);
                      return catId === 1 || catId === 3;
                    })
                    .map((subCat) => (
                      <MenuItem key={subCat.id} value={subCat.id}>
                        {subCat.accountCategoryName}
                      </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Bank Account */}
              <FormControl fullWidth variant="filled" sx={{ gridColumn: "span 2" }}>
                <InputLabel>Bank Account *</InputLabel>
                <Select
                  name="disbursementBankAccountId"
                  value={values.disbursementBankAccountId}
                  onChange={handleChange}
                  disabled={loadingAccounts || bankAccounts.length === 0}
                  error={touched.disbursementBankAccountId && !!errors.disbursementBankAccountId}
                >
                  {loadingAccounts && <MenuItem disabled><CircularProgress size={20} /></MenuItem>}
                  {bankAccounts.map((ba) => (
                    <MenuItem key={ba.id} value={ba.id}>
                      {ba.accountNumber} - {ba.accountName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Payment Method */}
              <FormControl fullWidth variant="filled" sx={{ gridColumn: "span 2" }}>
                <InputLabel>Payment Method *</InputLabel>
                <Select
                  name="paymentMethod"
                  value={values.paymentMethod}
                  onChange={(e) => setFieldValue("paymentMethod", e.target.value)}
                  onBlur={handleBlur}
                  error={touched.paymentMethod && !!errors.paymentMethod}
                >
                  {paymentMethods?.map((pm) => (
                    <MenuItem key={pm.id} value={pm.id}>
                      {pm.paymentType}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Payment Reference */}
              <TextField
                fullWidth
                variant="filled"
                label="Payment Reference "
                name="paymentReference"
                value={values.paymentReference}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.paymentReference && !!errors.paymentReference}
                helperText={touched.paymentReference && errors.paymentReference}
                sx={{ gridColumn: "span 2" }}
              />

              {/* Currency */}
              <FormControl fullWidth variant="filled" sx={{ gridColumn: "span 1" }}>
                <InputLabel>Currency *</InputLabel>
                <Select
                  name="currencyId"
                  value={values.currencyId}
                  onChange={handleChange}
                >
                  {currencies.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.currencyName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Exchange Rate (read-only) */}
              <TextField
                fullWidth
                variant="filled"
                label="Exchange Rate"
                value={values.exchangeRate}
                disabled
                sx={{ gridColumn: "span 1" }}
              />
            </Box>

            <Box display="flex" justifyContent="end" mt="20px" gap="10px">
              <Button onClick={onClose} disabled={isSubmitting}>
                Cancel
              </Button>
              <Button
                type="submit"
                color="secondary"
                variant="contained"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Disbursing..." : "Disburse"}
              </Button>
            </Box>
          </form>
        )}
      </Formik>
    </Box>
  );
};

export default ClaimDisbursementForm;
